import { useEffect, useState } from "react";
import { DataTable } from "../../../DataTable/DataTable";
import { Ban, CircleCheck, Search, Plus } from "lucide-react";
import { AddNewProductModal } from "../../../../shared/addnewproduct/AddNewProductModal";
import { useAuth } from "../../../../context/AuthContext";
import toast from "react-hot-toast";
import { InverntroyService } from "../../../../services/api/inventroy.service";
import { Eye } from "lucide-react";
import DemoImage from "../../../../assets/images/default_product.jpg";

export function StockProductsView() {
  const [isLoading, setIsLoading] = useState(false);
  const [productsList, setProductsList] = useState<any[]>([]);
  const [selectedProductId, setSelectedProduct] = useState<any | null>(null);
  const [showAddProductModal, setShowAddProductModal] = useState(false);
  const [selectedProductData, setSelectedProductData] = useState<any | null>(
    null
  );
  const [searchText, setSearchText] = useState("");
  const [debouncedSearchText, setDebouncedSearchText] = useState("");

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchText(searchText);
    }, 1000);

    return () => {
      clearTimeout(handler);
    };
  }, [searchText]);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [nextPage, setNextPage] = useState<number | null>(null);
  const [previousPage, setPreviousPage] = useState<number | null>(null);

  const { user } = useAuth();

  const isAdmin = user?.role === "admin";

  const loadProductListing = async (
    page: number = currentPage,
    size: number = pageSize,
    search: string = debouncedSearchText
  ) => {
    try {
      setIsLoading(true);
      const inventroyRes = await InverntroyService.getProductListing({
        page,
        pageSize: size,
        searchText: search,
      });
      if (inventroyRes?.success) {
        setProductsList(inventroyRes?.data);
        setCurrentPage(inventroyRes?.pagination?.currentPage);
        setTotalPages(inventroyRes?.pagination?.totalPages);
        setNextPage(inventroyRes?.pagination?.nextPage);
        setPreviousPage(inventroyRes?.pagination?.previousPage);
      } else {
        setProductsList(inventroyRes?.data || []);
      }
    } catch (error: unknown) {
      toast.error(`${error}`);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchText]);

  const handleOnSuccess = async () => {
    loadProductListing(currentPage, pageSize, debouncedSearchText);
  };

  const addProductButton = (
    <button
      onClick={() => setShowAddProductModal(true)}
      className="relative z-10 flex items-center justify-center px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors whitespace-nowrap"
    >
      <Plus className="w-5 h-5 mr-2" />
      <span className="whitespace-nowrap">Add Product</span>
    </button>
  );

  const fetchProductDetails = async (productId: string) => {
    try {
      const response = await InverntroyService.getProductDetails(productId);
      if (response.success) {
        setSelectedProductData(response.data);
      } else {
        toast.error("Failed to load product details.");
      }
    } catch (error) {
      console.log(error);
      toast.error("Error loading product details.");
    }
  };

  const toggleProductActiveStatus = async (product: any) => {
    try {
      const newStatus = !product.isActive;
      const detailsRes = await InverntroyService.getProductDetails(product.id);
      if (detailsRes?.success && detailsRes.data) {
        const d = detailsRes.data;
        const payload = new FormData();
        payload.append("productMstr", JSON.stringify({
          name: d.productMstr?.name,
          description: d.productMstr?.description,
          categoryId: d.productMstr?.categoryId,
          isPublished: d.productMstr?.isPublished,
          isActive: newStatus,
          isRxRequired: d.productMstr?.isRxRequired,
          tabletPerStripe: d.productMstr?.tabletPerStripe,
        }));

        const updateRes = await InverntroyService.updateProductActivityV2(product.id, newStatus);
        if (updateRes.success) {
          toast.success(`Product ${newStatus ? 'activated' : 'deactivated'} successfully`);
          loadProductListing(currentPage, pageSize, debouncedSearchText);
        } else {
          toast.error(updateRes.message || "Failed to update status");
        }
      } else {
        toast.error("Failed to fetch product details for status update");
      }
    } catch (error) {
      toast.error("Error updating status");
    }
  };

  const renderDescription = (desc: string) => {
    if (!desc) return "";
    const words = desc.split(" ");
    if (words.length <= 50) return desc;
    return words.slice(0, 50).join(" ") + ".....";
  };

  useEffect(() => {
    if (selectedProductId) {
      fetchProductDetails(selectedProductId);
    }
  }, [selectedProductId]);

  const columns = [
    {
      key: "name",
      title: "Product",
      width: "w-[260px]",
      render: (item: any) => (
        <div className="flex items-center space-x-3">
          <img
            src={item.image || DemoImage}
            alt={item.name}
            className="w-12 h-12 rounded-lg object-cover border border-gray-200"
          />
          <div
            className="text-sm font-medium line-clamp-2 cursor-default"
            title={item.name}
          >
            {item.name}
          </div>
        </div>
      ),
    },
    {
      key: "productDescription",
      title: "Description",
      width: "w-[280px]",
      render: (item: any) => (
        <div className="text-sm text-gray-600" title={item.description}>
          {renderDescription(item.description)}
        </div>
      ),
    },
    {
      key: "published",
      title: "Published",
      width: "w-[120px]",
      render: (item: any) => (
        <div>
          <div className="text-sm font-medium">
            {item.isPublished ? (
              <CircleCheck className="w-5 h-5 bg-[#199C4D] rounded-full text-[white]" />
            ) : (
              <Ban className="w-5 h-5 bg-[#c62a2a] rounded-full text-[white]" />
            )}
          </div>
        </div>
      ),
    },
    {
      key: "active",
      title: "Active",
      width: "w-[120px]",
      render: (item: any) => (
        <div className="flex items-center">
          <button
            onClick={() => toggleProductActiveStatus(item)}
            className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${item.isActive ? "bg-emerald-500" : "bg-gray-300"
              }`}
          >
            <span
              className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${item.isActive ? "translate-x-5" : "translate-x-1"
                }`}
            />
          </button>
        </div>
      ),
    },
    {
      key: "actions",
      title: "Actions",
      width: "w-[120px]",
      render: (item: any) => (
        <div className="flex space-x-4 items-center">
          <button
            onClick={() => {
              setSelectedProduct(item.id);
              setShowAddProductModal(true);
            }}
            className="text-blue-500 hover:text-blue-700"
            title="View / Edit Product"
          >
            <Eye className="w-5 h-5" />
          </button>
        </div>
      ),
    },
  ];

  useEffect(() => {
    loadProductListing(currentPage, pageSize, debouncedSearchText);
  }, [currentPage, pageSize, debouncedSearchText]);
  return (
    <div className="relative">
      {!showAddProductModal && (
        <>
          <div className="flex items-center justify-between mb-4">
            {isAdmin && addProductButton}

            <div className="relative flex items-center gap-2">
              <div>
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search products..."
                  value={searchText}
                  onChange={handleSearchChange}
                  className="border rounded-lg pl-10 pr-3 py-2.5 text-sm w-64"
                />
              </div>
            </div>
          </div>
        </>
      )}

      {showAddProductModal ? (
        <AddNewProductModal
          productId={selectedProductId}
          productData={selectedProductData}
          onSuccess={handleOnSuccess}
          onClose={() => {
            setShowAddProductModal(false);
            setSelectedProduct(null);
            setSelectedProductData(null);
          }}
          categoryId={1}
        />
      ) : (
        <DataTable
          data={productsList}
          columns={columns}
          itemsPerPage={10}
          isLoading={isLoading}
          customFilters={null}
          filterComponent={null}
          pagination={{
            currentPage,
            setCurrentPage,
            setTotalPages,
            totalPages,
            pageSize,
            setPageSize,
          }}
        />
      )}
    </div>
  );
}
